"use client";

import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import AnimatedDigit from "../animated_digit/AnimatedDigit";
import styles from "./scroll_indicator.module.scss";

interface ScrollIndicatorProps {
    sectionIds: readonly string[];
}

const RADIUS = 22;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

export default function ScrollIndicator({ sectionIds }: ScrollIndicatorProps) {
    const resolvedIds = useMemo(
        () => (sectionIds),
        [sectionIds]
    );
    const total = resolvedIds.length;

    const [currentIndex, setCurrentIndex] = useState<number>(-1);
    const [prevIndex, setPrevIndex] = useState<number>(-1);
    const [showPulse, setShowPulse] = useState(false);
    const pulseTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const animatingRef = useRef(false);

    // IntersectionObserver to track which section is in view
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        const id = entry.target.id;
                        const idx = resolvedIds.indexOf(id);
                        if (idx !== -1) {
                            setCurrentIndex(idx);
                        }
                    }
                }
            },
            {
                threshold: 0.35,
                rootMargin: "-10% 0px -10% 0px",
            }
        );

        const elements: Element[] = [];
        for (const id of resolvedIds) {
            const el = document.getElementById(id);
            if (el) {
                observer.observe(el);
                elements.push(el);
            }
        }

        return () => {
            for (const el of elements) {
                observer.unobserve(el);
            }
        };
    }, [resolvedIds]);

    // Trigger pulse + slide animation on index change
    useEffect(() => {
        if (currentIndex === prevIndex) return;

        if (animatingRef.current) {
            setPrevIndex(currentIndex);
            return;
        }

        animatingRef.current = true;
        setShowPulse(true);

        if (pulseTimeoutRef.current) {
            clearTimeout(pulseTimeoutRef.current);
        }

        pulseTimeoutRef.current = setTimeout(() => {
            setShowPulse(false);
            setPrevIndex(currentIndex);
            animatingRef.current = false;
        }, 350);

        return () => {
            if (pulseTimeoutRef.current) {
                clearTimeout(pulseTimeoutRef.current);
            }
        };
    }, [currentIndex, prevIndex]);

    const handleClick = useCallback(() => {
        if (currentIndex >= 0 && currentIndex < total) {
            const el = document.getElementById(resolvedIds[currentIndex]);
            el?.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, [currentIndex, total, resolvedIds]);

    // Progress arc calculation
    const progress = currentIndex >= 0 ? (currentIndex + 1) / total : 0;
    const dashOffset = CIRCUMFERENCE * (1 - progress);

    // Display values
    const displayCurrent = currentIndex >= 0 ? currentIndex + 1 : null;
    const isAnimating = prevIndex !== currentIndex && prevIndex !== -1;

    // Unique gradient ID per instance (useId is stable across SSR/client)
    const reactId = useId();
    const gradientId = `scrollIndicatorGradient${reactId}`;

    return (
        <div
            className={styles.indicator}
            onClick={handleClick}
            role="navigation"
            aria-label={`Section ${displayCurrent ?? "\u2014"} of ${total}`}
            title={
                currentIndex >= 0
                    ? resolvedIds[currentIndex].replace("section_", "").replace(/_/g, " ")
                    : undefined
            }
        >
            <svg className={styles.ring} viewBox="0 0 60 60">
                <defs>
                    <linearGradient
                        id={gradientId}
                        x1="0%"
                        y1="0%"
                        x2="100%"
                        y2="100%"
                    >
                        <stop offset="0%" stopColor="#0a84ff" />
                        <stop offset="100%" stopColor="#32D74B" />
                    </linearGradient>
                </defs>
                <circle
                    className={styles.ring_track}
                    cx="30"
                    cy="30"
                    r={RADIUS}
                />
                <circle
                    className={styles.ring_progress}
                    cx="30"
                    cy="30"
                    r={RADIUS}
                    strokeDasharray={CIRCUMFERENCE}
                    strokeDashoffset={dashOffset}
                    stroke={`url(#${gradientId})`}
                />
            </svg>

            <div className={styles.badge}>
                {showPulse && <div className={styles.pulse} key={currentIndex} />}

                <div className={styles.current}>
                    <AnimatedDigit value={displayCurrent?.toString() ?? "\u2014"} />
                </div>

                <span className={styles.fraction}>/ {total}</span>
            </div>
        </div>
    );
}
